import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import Image from 'src/components/image';
import Scrollbar from 'src/components/scrollbar';
import { TablePaginationCustom } from 'src/components/table';
import {
  GetReminderDto,
  GetReminderDtoCustomRecurrence,
  GetReminderDtoCustomRecurrenceUnitEnum,
  GetReminderDtoNotificationChannelsEnum,
  GetReminderDtoRecurrenceTypeEnum,
} from 'woofwoof-api';
import { translateKey } from 'src/locales/translate';
import {
  TAB_TABLE_CONTAINER_SX,
  TAB_TABLE_PAGINATION_SX,
  TAB_TABLE_ROWS_PER_PAGE_OPTIONS,
  TAB_TABLE_SCROLL_BODY_SX,
} from 'src/theme/tableContainer';

interface ProfileRemindersTableProps {
  data: GetReminderDto[];
  /**
   * Paging is driven from above: the rows arrive a page at a time from the
   * server, so the component holding the query holds the page state. `total`
   * counts every row, not the ones in `data`.
   */
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (event: unknown, page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRowClick: (reminderId: string) => void;
}

const DAY_NAMES = [
  translateKey('hardcoded.niedziele'),
  translateKey('openingStatus.weekdays.1'),
  translateKey('openingStatus.weekdays.2'),
  translateKey('hardcoded.srode'),
  translateKey('openingStatus.weekdays.4'),
  translateKey('openingStatus.weekdays.5'),
  translateKey('hardcoded.sobote'),
];

const WEEK_OF_MONTH_LABELS: Record<string, string> = {
  first: 'pierwszym',
  second: 'drugim',
  third: 'trzecim',
  fourth: 'czwartym',
  last: 'ostatnim',
};

export function formatRecurrence(
  type: GetReminderDtoRecurrenceTypeEnum,
  custom?: GetReminderDtoCustomRecurrence,
): string {
  switch (type) {
    case GetReminderDtoRecurrenceTypeEnum.Single:
      return 'Jednorazowo';
    case GetReminderDtoRecurrenceTypeEnum.Daily:
      return 'Codziennie';
    case GetReminderDtoRecurrenceTypeEnum.Monthly:
      return translateKey('hardcoded.miesiecznie');
    case GetReminderDtoRecurrenceTypeEnum.Yearly:
      return 'Rocznie';
    case GetReminderDtoRecurrenceTypeEnum.Custom: {
      if (!custom) return 'Niestandardowe';
      const { every, unit, dayOfWeek, weekOfMonth } = custom;
      const lastTwo = every % 100;
      const isTeen = lastTwo >= 12 && lastTwo <= 14;
      const lastDigit = every % 10;
      const few = !isTeen && lastDigit >= 2 && lastDigit <= 4;
      const unitLabel =
        unit === GetReminderDtoCustomRecurrenceUnitEnum.Hour
          ? every === 1
            ? translateKey('hardcoded.godzine')
            : few
            ? 'godziny'
            : 'godzin'
          : unit === GetReminderDtoCustomRecurrenceUnitEnum.Day
          ? every === 1
            ? translateKey('hardcoded.dzien')
            : 'dni'
          : unit === GetReminderDtoCustomRecurrenceUnitEnum.Week
          ? every === 1
            ? translateKey('hardcoded.tydzien')
            : few
            ? 'tygodnie'
            : 'tygodni'
          : every === 1
          ? translateKey('hardcoded.miesiac')
          : few
          ? translateKey('hardcoded.miesiace')
          : translateKey('hardcoded.miesiecy');
      let label = translateKey('hardcoded.everyInterval', {
        count: every,
        unit: unitLabel,
      });
      if (dayOfWeek !== undefined && dayOfWeek !== null) {
        label += translateKey('hardcoded.onWeekday', {
          day: DAY_NAMES[dayOfWeek],
        });
      }
      if (weekOfMonth) {
        label += translateKey('hardcoded.weekOfMonthSuffix', {
          week: WEEK_OF_MONTH_LABELS[weekOfMonth] ?? weekOfMonth,
        });
      }
      return label;
    }
    default:
      return type;
  }
}

export default function ProfileRemindersTable({
  data,
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
}: ProfileRemindersTableProps) {
  // `data` is already this page: the server sliced it.
  const paginated = data;

  const TableHeader = (
    <TableHead>
      <TableRow>
        <TableCell>{translateKey('search.ui.aboutTab')}</TableCell>
        <TableCell>{translateKey('visit.tableDate')}</TableCell>
        <TableCell>Powtarzanie</TableCell>
        <TableCell>{translateKey('visit.tableKind')}</TableCell>
        <TableCell>Dodano przez</TableCell>
      </TableRow>
    </TableHead>
  );

  return (
    <>
      <TableContainer sx={TAB_TABLE_CONTAINER_SX}>
        {paginated.length > 0 ? (
          <Box sx={TAB_TABLE_SCROLL_BODY_SX}>
            <Scrollbar>
              <Table stickyHeader>
                {TableHeader}
                <TableBody>
                  {paginated.map((reminder) => (
                    <TableRow
                      key={reminder.id}
                      hover
                      data-testid="reminder-row"
                      data-reminder-id={reminder.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => onRowClick(reminder.id)}
                    >
                      <Tooltip title={reminder.description} arrow>
                        <TableCell
                          sx={{
                            maxWidth: 240,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {reminder.description}
                        </TableCell>
                      </Tooltip>
                      <TableCell>
                        {format(
                          new Date(reminder.scheduledAt),
                          'dd/MM/yyyy HH:mm',
                          { locale: pl },
                        )}
                      </TableCell>
                      <TableCell>
                        {formatRecurrence(
                          reminder.recurrenceType,
                          reminder.customRecurrence,
                        )}
                      </TableCell>
                      <TableCell>
                        {reminder.notificationChannels
                          .map((ch) =>
                            ch === GetReminderDtoNotificationChannelsEnum.Email
                              ? translateKey('search.book.cardEmail')
                              : 'SMS',
                          )
                          .join(', ')}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {reminder.creatorName}
                        </Typography>
                        {reminder.creatorOrganizationName && (
                          <Typography variant="caption" color="text.secondary">
                            {reminder.creatorOrganizationName}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Scrollbar>
          </Box>
        ) : (
          <>
            <Table stickyHeader>{TableHeader}</Table>
            <Stack
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flex="1"
              gap={2}
            >
              <Image
                sx={{ width: '200px' }}
                alt="empty reminders"
                src="/assets/images/empty_table.svg"
              />
              <Typography>
                {translateKey('hardcoded.brak_przypomnien')}
              </Typography>
            </Stack>
          </>
        )}
      </TableContainer>
      <TablePaginationCustom
        count={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={TAB_TABLE_ROWS_PER_PAGE_OPTIONS}
        sx={TAB_TABLE_PAGINATION_SX}
      />
    </>
  );
}
